"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState, KeyboardEvent, useId } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useClickAway, useDebounce, useKeyPressEvent } from "react-use";
import { CommunityMemberMinimal } from "@/entities/IProfile";
import { commonApiFetch } from "@/services/api/common-api";
import CommonProfileSearchItems from "../profile-search/CommonProfileSearchItems";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { getSelectableIdentity } from "@/components/utils/input/profile-search/getSelectableIdentity";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
export enum IdentitySearchSize {
  SM = "SM",
  MD = "MD",
}
const MIN_SEARCH_LENGTH = 3;
export default function IdentitySearch(t0) {
  const $ = _c(92);
  const {
    identity,
    size: t1,
    label: t2,
    error: t3,
    autoFocus: t4,
    setIdentity
  } = t0;
  const size = t1 === undefined ? IdentitySearchSize.MD : t1;
  const label = t2 === undefined ? "Identity" : t2;
  const error = t3 === undefined ? false : t3;
  const autoFocus = t4 === undefined ? false : t4;
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      [IdentitySearchSize.SM]: "tw-py-3 tw-text-sm",
      [IdentitySearchSize.MD]: "tw-pb-3 tw-pt-3 tw-text-md"
    };
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const INPUT_CLASSES = t5;
  let t6;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      [IdentitySearchSize.SM]: "tw-text-sm",
      [IdentitySearchSize.MD]: "tw-text-md"
    };
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const LABEL_CLASSES = t6;
  let t7;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      [IdentitySearchSize.SM]: "tw-top-3",
      [IdentitySearchSize.MD]: "tw-top-3.5"
    };
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  const ICON_CLASSES = t7;
  const inputId = useId();
  const listboxId = `${inputId}-listbox`;
  const [searchCriteria, setSearchCriteria] = useState(identity);
  const [debouncedValue, setDebouncedValue] = useState(searchCriteria);
  let t8;
  if ($[3] !== searchCriteria) {
    t8 = () => setDebouncedValue(searchCriteria);
    $[3] = searchCriteria;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  let t9;
  if ($[5] !== searchCriteria) {
    t9 = [searchCriteria];
    $[5] = searchCriteria;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  useDebounce(t8, 200, t9);
  let t10;
  let t11;
  if ($[7] !== debouncedValue) {
    t10 = [QueryKey.PROFILE_SEARCH, {
      param: debouncedValue,
      only_profile_owners: "true"
    }];
    t11 = async () => await commonApiFetch({
      endpoint: "community-members",
      params: {
        param: debouncedValue ?? "",
        only_profile_owners: "true"
      }
    });
    $[7] = debouncedValue;
    $[8] = t10;
    $[9] = t11;
  } else {
    t10 = $[8];
    t11 = $[9];
  }
  const t12 = !!debouncedValue && debouncedValue.length >= MIN_SEARCH_LENGTH;
  let t13;
  if ($[10] !== t10 || $[11] !== t11 || $[12] !== t12) {
    t13 = {
      queryKey: t10,
      queryFn: t11,
      enabled: t12
    };
    $[10] = t10;
    $[11] = t11;
    $[12] = t12;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  const {
    data
  } = useQuery(t13);
  const selectionUpdateRef = useRef(false);
  let t14;
  let t15;
  if ($[14] !== identity) {
    t14 = () => {
      if (selectionUpdateRef.current) {
        selectionUpdateRef.current = false;
        return;
      }
      setSearchCriteria(identity);
    };
    t15 = [identity];
    $[14] = identity;
    $[15] = t14;
    $[16] = t15;
  } else {
    t14 = $[15];
    t15 = $[16];
  }
  useEffect(t14, t15);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(null);
  const [highlightedOptionId, setHighlightedOptionId] = useState(undefined);
  const [shouldSubmit, setShouldSubmit] = useState(false);
  let t16;
  if ($[17] !== setIdentity) {
    t16 = (newValue, displayValue) => {
      selectionUpdateRef.current = true;
      setIdentity(newValue);
      setSearchCriteria(displayValue ?? newValue);
      setIsOpen(false);
      setHighlightedIndex(null);
    };
    $[17] = setIdentity;
    $[18] = t16;
  } else {
    t16 = $[18];
  }
  const onValueChange = t16;
  let t17;
  if ($[19] !== searchCriteria?.length) {
    t17 = newV => {
      if (newV) {
        const len = searchCriteria?.length ?? 0;
        setIsOpen(len >= MIN_SEARCH_LENGTH);
        return;
      }
      setIsOpen(false);
      setHighlightedIndex(null);
    };
    $[19] = searchCriteria?.length;
    $[20] = t17;
  } else {
    t17 = $[20];
  }
  const onFocusChange = t17;
  let t18;
  if ($[21] !== setIdentity) {
    t18 = newV_0 => {
      setSearchCriteria(newV_0);
      const len_0 = newV_0?.length ?? 0;
      setIsOpen(len_0 >= MIN_SEARCH_LENGTH);
      if (!newV_0) {
        setIdentity(null);
      }
      setHighlightedIndex(null);
    };
    $[21] = setIdentity;
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  const onSearchCriteriaChange = t18;
  let t19;
  if ($[23] !== onValueChange) {
    t19 = profile => {
      const nextIdentity = getSelectableIdentity(profile);
      if (!nextIdentity) {
        return false;
      }
      const displayValue_0 = profile.handle ?? profile.display ?? nextIdentity;
      onValueChange(nextIdentity, displayValue_0);
      return true;
    };
    $[23] = onValueChange;
    $[24] = t19;
  } else {
    t19 = $[24];
  }
  const selectProfile = t19;
  const wrapperRef = useRef(null);
  let t20;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => setIsOpen(false);
    $[25] = t20;
  } else {
    t20 = $[25];
  }
  useClickAway(wrapperRef, t20);
  let t21;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => setIsOpen(false);
    $[26] = t21;
  } else {
    t21 = $[26];
  }
  useKeyPressEvent("Escape", t21);
  const inputRef = useRef(null);
  const shouldAutoFocus = useRef(autoFocus);
  let t22;
  let t23;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = () => {
      if (shouldAutoFocus.current) {
        inputRef.current?.focus();
      }
    };
    t23 = [];
    $[27] = t22;
    $[28] = t23;
  } else {
    t22 = $[27];
    t23 = $[28];
  }
  useEffect(t22, t23);
  let t24;
  let t25;
  if ($[29] !== shouldSubmit) {
    t24 = () => {
      if (!shouldSubmit) {
        return;
      }
      const formElement = inputRef.current?.form;
      if (formElement) {
        formElement.requestSubmit();
      }
      setShouldSubmit(false);
    };
    t25 = [shouldSubmit];
    $[29] = shouldSubmit;
    $[30] = t24;
    $[31] = t25;
  } else {
    t24 = $[30];
    t25 = $[31];
  }
  useEffect(t24, t25);
  let t26;
  if ($[32] !== data || $[33] !== highlightedIndex || $[34] !== selectProfile) {
    t26 = event => {
      if (!data?.length) {
        return;
      }
      const maxIndex = data.length - 1;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setIsOpen(true);
        setHighlightedIndex(current => {
          if (current === null || current >= maxIndex) {
            return 0;
          }
          return current + 1;
        });
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setIsOpen(true);
        setHighlightedIndex(current_0 => {
          if (current_0 === null || current_0 <= 0) {
            return maxIndex;
          }
          return current_0 - 1;
        });
        return;
      }
      if (event.key === "Enter" && highlightedIndex !== null) {
        event.preventDefault();
        const profile_0 = data[highlightedIndex];
        if (profile_0) {
          if (selectProfile(profile_0)) {
            setShouldSubmit(true);
          }
        }
      }
    };
    $[32] = data;
    $[33] = highlightedIndex;
    $[34] = selectProfile;
    $[35] = t26;
  } else {
    t26 = $[35];
  }
  const handleArrowNavigation = t26;
  let t27;
  let t28;
  if ($[36] !== isOpen) {
    t27 = () => {
      if (!isOpen) {
        setHighlightedIndex(null);
      }
    };
    t28 = [isOpen];
    $[36] = isOpen;
    $[37] = t27;
    $[38] = t28;
  } else {
    t27 = $[37];
    t28 = $[38];
  }
  useEffect(t27, t28);
  let t29;
  let t30;
  if ($[39] !== data || $[40] !== identity) {
    t29 = () => {
      if (!data?.length) {
        setHighlightedIndex(null);
        return;
      }
      if (identity) {
        const matchingIndex = data.findIndex(profile_1 => {
          const value = getSelectableIdentity(profile_1);
          return value?.toLowerCase() === identity.toLowerCase();
        });
        if (matchingIndex >= 0) {
          setHighlightedIndex(matchingIndex);
          return;
        }
      }
      setHighlightedIndex(current_1 => current_1 === null ? null : Math.min(current_1, data.length - 1));
    };
    t30 = [data, identity];
    $[39] = data;
    $[40] = identity;
    $[41] = t29;
    $[42] = t30;
  } else {
    t29 = $[41];
    t30 = $[42];
  }
  useEffect(t29, t30);
  const t31 = searchCriteria ?? "";
  let t32;
  if ($[43] !== onSearchCriteriaChange) {
    t32 = e => onSearchCriteriaChange(e.target.value);
    $[43] = onSearchCriteriaChange;
    $[44] = t32;
  } else {
    t32 = $[44];
  }
  let t33;
  let t34;
  if ($[45] !== onFocusChange) {
    t33 = () => onFocusChange(true);
    t34 = e_0 => {
      const next = e_0.relatedTarget as Node | null;
      if (!next || !wrapperRef.current?.contains(next)) {
        onFocusChange(false);
      }
    };
    $[45] = onFocusChange;
    $[46] = t33;
    $[47] = t34;
  } else {
    t33 = $[46];
    t34 = $[47];
  }
  let t35;
  if ($[48] !== handleArrowNavigation) {
    t35 = event_0 => handleArrowNavigation(event_0);
    $[48] = handleArrowNavigation;
    $[49] = t35;
  } else {
    t35 = $[49];
  }
  const t36 = isOpen && highlightedOptionId ? highlightedOptionId : undefined;
  const t37 = `${INPUT_CLASSES[size]} ${error ? "tw-ring-error focus:tw-border-error focus:tw-ring-error tw-caret-error" : "tw-ring-iron-700 focus:tw-border-blue-500 tw-caret-primary-400 focus:tw-ring-primary-400 hover:tw-ring-iron-650"} tw-form-input tw-block tw-w-full tw-text-base tw-rounded-lg tw-border-0 tw-appearance-none tw-font-medium tw-border-iron-700 tw-peer tw-pl-10 tw-pr-4 tw-bg-iron-900 tw-shadow-sm tw-ring-1 tw-ring-inset placeholder:tw-text-iron-500 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset tw-transition tw-duration-300 tw-ease-out ${searchCriteria ? "focus:tw-text-white tw-text-primary-400" : "tw-text-white"}`;
  let t38;
  if ($[50] !== inputId || $[51] !== isOpen || $[52] !== listboxId || $[53] !== t31 || $[54] !== t32 || $[55] !== t33 || $[56] !== t34 || $[57] !== t35 || $[58] !== t36 || $[59] !== t37) {
    t38 = <input ref={inputRef} type="text" value={t31} onChange={t32} onFocus={t33} onBlur={t34} onKeyDown={t35} id={inputId} autoComplete="off" role="combobox" aria-autocomplete="list" aria-expanded={isOpen} aria-controls={listboxId} aria-activedescendant={t36} className={t37} placeholder=" " />;
    $[50] = inputId;
    $[51] = isOpen;
    $[52] = listboxId;
    $[53] = t31;
    $[54] = t32;
    $[55] = t33;
    $[56] = t34;
    $[57] = t35;
    $[58] = t36;
    $[59] = t37;
    $[60] = t38;
  } else {
    t38 = $[60];
  }
  const t39 = `${ICON_CLASSES[size]} tw-text-iron-300 tw-pointer-events-none tw-absolute tw-left-3 tw-h-5 tw-w-5`;
  let t40;
  if ($[61] !== t39) {
    t40 = <MagnifyingGlassIcon className={t39} aria-hidden="true" />;
    $[61] = t39;
    $[62] = t40;
  } else {
    t40 = $[62];
  }
  let t41;
  if ($[63] !== identity?.length || $[64] !== onValueChange || $[65] !== size) {
    t41 = !!identity?.length && <button type="button" aria-label="Clear identity" onClick={() => onValueChange(null)} className={`${ICON_CLASSES[size]} tw-absolute tw-right-3 tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-cursor-pointer tw-bg-transparent tw-border-0 tw-p-0 tw-text-iron-400 hover:tw-text-error focus:tw-outline-none focus:tw-ring-0 tw-transition tw-duration-300 tw-ease-out`}><FontAwesomeIcon icon={faXmark} className="tw-h-5 tw-w-5" /></button>;
    $[63] = identity?.length;
    $[64] = onValueChange;
    $[65] = size;
    $[66] = t41;
  } else {
    t41 = $[66];
  }
  const t42 = `${LABEL_CLASSES[size]} ${error ? "peer-focus:tw-text-error peer-placeholder-shown:-tw-translate-y-1/4 peer-placeholder-shown:tw-top-1/4" : "peer-focus:tw-text-primary-400 peer-placeholder-shown:-tw-translate-y-1/2 peer-placeholder-shown:tw-top-1/2"} tw-absolute tw-rounded-lg tw-cursor-text tw-font-medium tw-text-iron-500 tw-duration-300 tw-transform -tw-translate-y-4 tw-scale-75 tw-top-2 tw-z-10 tw-origin-[0] tw-bg-iron-900  peer-focus:tw-bg-iron-900 tw-ml-7 tw-px-2 peer-focus:tw-px-2 peer-focus:tw-text-primary-400 peer-placeholder-shown:tw-scale-100  peer-placeholder-shown:-tw-translate-y-1/2 peer-placeholder-shown:tw-top-1/2 peer-focus:tw-top-2 peer-focus:tw-scale-75 peer-focus:-tw-translate-y-4 rtl:peer-focus:tw-translate-x-1/4 rtl:peer-focus:tw-left-auto tw-start-1`;
  let t43;
  if ($[67] !== inputId || $[68] !== label || $[69] !== t42) {
    t43 = <label htmlFor={inputId} className={t42}>{label}</label>;
    $[67] = inputId;
    $[68] = label;
    $[69] = t42;
    $[70] = t43;
  } else {
    t43 = $[70];
  }
  let t44;
  if ($[71] !== data) {
    t44 = data ?? [];
    $[71] = data;
    $[72] = t44;
  } else {
    t44 = $[72];
  }
  let t45;
  if ($[73] !== selectProfile) {
    t45 = profile_2 => {
      if (!profile_2) {
        return;
      }
      selectProfile(profile_2);
    };
    $[73] = selectProfile;
    $[74] = t45;
  } else {
    t45 = $[74];
  }
  let t46;
  if ($[75] !== highlightedIndex || $[76] !== identity || $[77] !== isOpen || $[78] !== listboxId || $[79] !== searchCriteria || $[80] !== t44 || $[81] !== t45) {
    t46 = <CommonProfileSearchItems open={isOpen} selected={identity} searchCriteria={searchCriteria} profiles={t44} highlightedIndex={highlightedIndex} listboxId={listboxId} onHighlightedOptionIdChange={setHighlightedOptionId} onProfileSelect={t45} />;
    $[75] = highlightedIndex;
    $[76] = identity;
    $[77] = isOpen;
    $[78] = listboxId;
    $[79] = searchCriteria;
    $[80] = t44;
    $[81] = t45;
    $[82] = t46;
  } else {
    t46 = $[82];
  }
  let t47;
  if ($[83] !== error) {
    t47 = error && <div className="tw-pt-1.5 tw-relative tw-flex tw-items-center tw-gap-x-2"><FontAwesomeIcon icon={faCircleExclamation} className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-text-error" aria-hidden="true" /><div className="tw-text-error tw-text-xs tw-font-medium">Please enter identity</div></div>;
    $[83] = error;
    $[84] = t47;
  } else {
    t47 = $[84];
  }
  let t48;
  if ($[85] !== t38 || $[86] !== t40 || $[87] !== t41 || $[88] !== t43 || $[89] !== t46 || $[90] !== t47) {
    t48 = <div className="tw-group tw-w-full tw-relative" ref={wrapperRef}>{t38}{t40}{t41}{t43}{t46}{t47}</div>;
    $[85] = t38;
    $[86] = t40;
    $[87] = t41;
    $[88] = t43;
    $[89] = t46;
    $[90] = t47;
    $[91] = t48;
  } else {
    t48 = $[91];
  }
  return t48;
}